'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Search, Bell, Play, Menu, X, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Crypto', href: '/crypto' },
  { label: 'DeFi', href: '/defi' },
  { label: 'Web3', href: '/web3' },
  { label: 'Markets', href: '/markets' },
  { label: 'LiveTV', href: '/livetv', live: true },
  { label: 'Education', href: '/education' },
];

const moreLinks = [
  { label: 'Contact Us', href: '/contact' },
  { label: 'About Us', href: '/about' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Cookie Policy', href: '/cookies' },
];

interface Notification {
  id: number;
  emoji: string;
  label: string;
  title: string;
  time: string;
  href: string;
  read: boolean;
}

const initialNotifications: Notification[] = [
  {
    id: 1,
    emoji: '🔴',
    label: 'Breaking',
    title: 'SEC Approves Spot Ethereum ETF Applications',
    time: '10m ago',
    href: '/blog/sec-approves-spot-ethereum-etf-applications',
    read: false,
  },
  {
    id: 2,
    emoji: '📈',
    label: 'Market Alert',
    title: 'Bitcoin surges past $105,000 all-time high',
    time: '25m ago',
    href: '/blog/bitcoin-hits-new-all-time-high',
    read: false,
  },
  {
    id: 3,
    emoji: '📺',
    label: 'Live TV',
    title: 'DMTV Crypto Daily Show is now LIVE',
    time: '1h ago',
    href: '/livetv',
    read: false,
  },
];

export default function TopNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const moreRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname?.startsWith(href);
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
    setNotifOpen(false);
  };

  const handleNotifClick = (href: string) => {
    setNotifOpen(false);
    router.push(href);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-black border-b border-border">
      <div className="w-full max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="/assets/images/DMTV_Official_Logo-1789695605720.png"
              alt="DMTV Digital Money TV"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.label}`}
                href={link?.href}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors duration-150 rounded
                  ${isActive(link?.href)
                    ? 'text-primary border-b-2 border-primary rounded-none' :'text-foreground hover:text-primary'
                  }`}
              >
                {link?.label}
                {link?.live && (
                  <span className="bg-primary text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider live-pulse">
                    LIVE
                  </span>
                )}
              </Link>
            ))}

            {/* More Dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors duration-150 rounded
                  ${moreLinks.some(l => isActive(l.href)) ? 'text-primary' : 'text-foreground hover:text-primary'}`}
              >
                More <ChevronDown size={14} className={`transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-xl z-50 py-1">
                  {moreLinks.map((link) => (
                    <Link
                      key={`more-${link.label}`}
                      href={link.href}
                      onClick={() => setMoreOpen(false)}
                      className={`block px-4 py-2.5 text-sm transition-colors duration-150
                        ${isActive(link.href) ? 'text-primary bg-primary/10' : 'text-foreground hover:bg-muted hover:text-primary'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
              aria-label="Search"
            >
              <Search size={18} />
            </button>

            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                aria-label="Notifications"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full text-[9px] text-white font-bold flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-[#111111] border border-[#2a2a2a] rounded-xl shadow-2xl z-50 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a]">
                    <span className="text-white font-semibold text-sm">Notifications</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                      >
                        Mark all as read
                      </button>
                      <button
                        onClick={handleClearAll}
                        className="text-xs text-muted-foreground hover:text-white transition-colors font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Notification Items */}
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => handleNotifClick(notif.href)}
                          className={`w-full text-left px-4 py-3 border-b border-[#1e1e1e] hover:bg-[#1a1a1a] transition-colors duration-150 flex items-start gap-3 group ${
                            notif.read ? 'opacity-60' : ''
                          }`}
                        >
                          <span className="text-lg flex-shrink-0 mt-0.5">{notif.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
                                {notif.label}
                              </span>
                              {!notif.read && (
                                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-white text-xs font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                              {notif.title}
                            </p>
                            <span className="text-muted-foreground text-[10px] mt-1 block">
                              {notif.time}
                            </span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Link
              href="/livetv"
              className="hidden sm:flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-4 py-2 rounded text-sm font-semibold transition-all duration-150 active:scale-95"
            >
              <Play size={14} fill="white" />
              Watch Live TV
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="pb-3">
            <input
              type="text"
              placeholder="Search crypto news, coins, markets..."
              className="w-full bg-muted border border-border rounded px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-card border-t border-border px-4 pb-4">
          <nav className="flex flex-col gap-1 pt-2">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                className={`flex items-center gap-2 px-3 py-2.5 rounded text-sm font-medium transition-colors duration-150
                  ${isActive(link?.href) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted hover:text-primary'}`}
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
                {link?.live && (
                  <span className="bg-primary text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                    LIVE
                  </span>
                )}
              </Link>
            ))}
            <div className="border-t border-border pt-2 mt-1">
              {moreLinks.map((link) => (
                <Link
                  key={`mobile-more-${link.label}`}
                  href={link.href}
                  className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors duration-150"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <Link
              href="/livetv"
              className="flex items-center justify-center gap-2 bg-accent text-white px-4 py-2.5 rounded text-sm font-semibold mt-2 transition-all duration-150 active:scale-95"
              onClick={() => setMobileOpen(false)}
            >
              <Play size={14} fill="white" />
              Watch Live TV
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}